import { ChainId } from '@src/configs/chainInfo'
import { IToken } from '@src/types/token'

/**
 * Computes the USD value of a Bitcoin amount
 * This is used as an interim solution until the backend supports Bitcoin price data
 *
 * @param amount - The amount in Bitcoin (in satoshis/smallest unit)
 * @param price - The current price of Bitcoin in USD
 * @param token - The token object containing decimals information
 * @returns The USD value as a string, or undefined if parameters are invalid
 */
export const computeBitcoinUSDAmount = (
    amount: bigint | undefined,
    price: number | null,
    token: IToken
): string | undefined => {
    if (!amount || amount === BigInt(0) || !price || price <= 0) {
        return undefined
    }

    // Convert from satoshis (or smallest unit) to BTC based on token decimals
    const decimals = token.decimals || 8 // Default to 8 for Bitcoin if not specified
    const btcAmount = Number(amount) / Math.pow(10, decimals)

    // Calculate USD value
    const usdValue = btcAmount * price

    // Format based on value
    if (usdValue < 0.01) {
        return usdValue.toFixed(6)
    } else if (usdValue < 1) {
        return usdValue.toFixed(4)
    } else {
        return usdValue.toFixed(2)
    }
}

/**
 * Checks if a token is a Bitcoin chain asset
 *
 * @param token - The token to check
 * @returns True if the token is on the Bitcoin chain
 */
export const isBitcoinAsset = (token?: IToken): boolean => {
    return token?.chainId === ChainId.Bitcoin
}
